import { resolveRpLink, RP_LIST_CAPS, type RpSectionOf } from '@mlm/shared';
import { cn } from '../../lib/cn';
import { useRpCtx } from '../context';
import { hasRpMedia, RpLinkButton, RpMedia } from '../primitives';
import { RP_IMAGE_MAX_W, RP_SPACER_H, rpAlign, rpJustify } from '../tokens';

/**
 * The six primitive sections. They are grouped in one file because each is a
 * handful of lines and they share one obligation: **these are where the five
 * pre-builder block types land after conversion**, so their markup mirrors what
 * the member page has always rendered. Changing a class here changes how every
 * legacy page looks — check `legacy-snapshot.test.ts` before you do.
 *
 * Each section applies its own `align` to its content root (rather than
 * inheriting it from a band wrapper) so that the `version: 0` bare render path
 * puts the class on exactly the element the old renderer put it on.
 */

const HEADING_LEVEL: Record<1 | 2 | 3, string> = {
  // Level 1 reproduces the legacy `heading` block exactly.
  1: 'text-3xl font-bold',
  2: 'text-2xl font-bold',
  3: 'text-xl font-semibold',
};

export function RpHeading({ section }: { section: RpSectionOf<'heading'> }) {
  const c = section.config;
  const align = rpAlign(section.style?.align);
  const cls = cn('font-heading', HEADING_LEVEL[c.level] ?? HEADING_LEVEL[2], align);
  const body = (
    <>
      {c.eyebrow ? (
        <p className={cn('text-xs font-semibold uppercase tracking-widest opacity-70', align)}>
          {c.eyebrow}
        </p>
      ) : null}
      {c.level === 1 ? (
        <h1 className={cls}>{c.text}</h1>
      ) : c.level === 2 ? (
        <h2 className={cls}>{c.text}</h2>
      ) : (
        <h3 className={cls}>{c.text}</h3>
      )}
      {c.subheading ? <p className={cn('text-base opacity-80', align)}>{c.subheading}</p> : null}
    </>
  );
  // No wrapper when there is nothing to space — keeps the converted legacy
  // heading a bare <h1>, exactly as before.
  return c.eyebrow || c.subheading ? <div className="space-y-2">{body}</div> : body;
}

export function RpRichText({ section }: { section: RpSectionOf<'richText'> }) {
  const ctx = useRpCtx();
  const c = section.config;
  const align = rpAlign(section.style?.align);
  if (typeof c.html === 'string' && c.html) {
    return (
      <div
        className={cn('prose max-w-none dark:prose-invert', align)}
        // `renderHtml` is host-supplied: it absolutizes origin-relative inline
        // image srcs and sanitizes. The API already sanitized on write; this is
        // defense-in-depth, and it means the admin preview is never a
        // trusted-HTML mode either.
        dangerouslySetInnerHTML={{ __html: ctx.renderHtml(c.html) }}
      />
    );
  }
  // The pre-WYSIWYG plain-text path, preserved verbatim.
  if (typeof c.text === 'string' && c.text) {
    return (
      <p className={cn('whitespace-pre-wrap text-base text-muted-foreground', align)}>{c.text}</p>
    );
  }
  return null;
}

export function RpImage({ section }: { section: RpSectionOf<'image'> }) {
  const ctx = useRpCtx();
  const c = section.config;
  const align = rpAlign(section.style?.align);
  if (!hasRpMedia(c.media)) return null;
  const img = (
    <RpMedia
      media={c.media}
      className={cn('inline-block', RP_IMAGE_MAX_W[c.maxWidth ?? 'full'] ?? RP_IMAGE_MAX_W.full)}
    />
  );
  const href = c.link ? resolveRpLink(c.link, ctx.link) : null;
  return (
    <div className={align}>
      {href && href !== '#' ? <a href={href}>{img}</a> : img}
      {c.caption ? <p className="mt-2 text-sm opacity-70">{c.caption}</p> : null}
    </div>
  );
}

export function RpVideo({ section }: { section: RpSectionOf<'video'> }) {
  const c = section.config;
  if (!c.url) return null;
  return (
    <div>
      <RpMedia media={{ kind: 'video', url: c.url }} aspect={c.aspect ?? '16:9'} />
      {c.caption ? (
        <p className={cn('mt-2 text-sm opacity-70', rpAlign(section.style?.align))}>{c.caption}</p>
      ) : null}
    </div>
  );
}

export function RpButtonGroup({ section }: { section: RpSectionOf<'buttonGroup'> }) {
  const c = section.config;
  const buttons = c.buttons.slice(0, RP_LIST_CAPS.buttonGroupButtons);
  if (!buttons.length) return null;
  return (
    <div
      className={cn(
        'flex flex-wrap items-center gap-3',
        c.layout === 'stack' ? 'flex-col' : 'flex-row',
        rpJustify(section.style?.align),
      )}
    >
      {buttons.map((b, i) => (
        <RpLinkButton key={i} button={b} />
      ))}
    </div>
  );
}

export function RpSpacer({ section }: { section: RpSectionOf<'spacer'> }) {
  const c = section.config;
  if (c.divider) return <hr className="border-t opacity-40" />;
  return <div aria-hidden className={RP_SPACER_H[c.height] ?? RP_SPACER_H.md} />;
}
